import { useCallback, useContext } from 'react';
import { NetworkError } from 'types/networkError';
import { isBlank } from 'utils/isBlank';
import { isPresent } from 'utils/isPresent';

import { OrganizationContext } from 'components/AdminNew/Companies/CompanyDetails/OrganizationContext';

import { CreditCardFormContext, CreditCardFormDispatchContext } from '../CreditCardFormProvider';

import useAddCreditCard from './useAddCreditCard';

const useSubmitToStripe = (
  stripe: Stripe,
  isPrimary: boolean,
  onSuccess: () => void,
  onError: () => void,
  source?: string
) => {
  const { organizationId, triggerContext: context } = useContext(OrganizationContext);
  const { nameOnCard } = useContext(CreditCardFormContext);
  const { setNameOnCardError, setGeneralStripeError } = useContext(CreditCardFormDispatchContext);

  const { mutateAsync: addCreditCard } = useAddCreditCard([context, source].join(' - '));

  return useCallback(() => {
    if (isBlank(nameOnCard)) {
      setNameOnCardError('Name is required.');
      onError();
      return false;
    }

    stripe.createToken({ name: nameOnCard }).then(async ({ token, error }) => {
      if (isPresent(token)) {
        try {
          await addCreditCard({
            orgId: organizationId,
            stripe_card_token: token.id,
            primary: isPrimary,
          });

          onSuccess();
        } catch (err: unknown) {
          setGeneralStripeError(
            (err as NetworkError<{ error: string }>).response?.data.error ??
              'There was an error while trying to add new credit card'
          );

          onError();
        }
      } else if (isPresent(error)) {
        onError();
        setGeneralStripeError(error?.message ?? 'There was an error while trying to add new credit card');
      }
    });
  }, [
    nameOnCard,
    stripe,
    setNameOnCardError,
    onError,
    addCreditCard,
    organizationId,
    isPrimary,
    onSuccess,
    setGeneralStripeError,
  ]);
};

export default useSubmitToStripe;
